import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Loader2, History, User, Clock, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
    create: { label: 'Creacion', variant: 'default' },
    update: { label: 'Edicion', variant: 'secondary' },
    delete: { label: 'Borrado', variant: 'destructive' },
    close: { label: 'Cierre', variant: 'default' },
    deactivate: { label: 'Baja logica', variant: 'destructive' },
    block_delete: { label: 'Borrado bloqueado', variant: 'destructive' },
    status_change: { label: 'Cambio de estado', variant: 'secondary' },
};

const fmtValue = (v) => {
    if (v === null || v === undefined) return <span className="text-muted-foreground italic">vacio</span>;
    if (typeof v === 'boolean') return v ? 'si' : 'no';
    if (typeof v === 'object') {
        try { return <code className="text-xs">{JSON.stringify(v)}</code>; }
        catch (e) { return String(v); }
    }
    const s = String(v);
    return s.length > 80 ? s.slice(0, 80) + '…' : s;
};

const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleString('es-ES', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    } catch (e) { return iso; }
};

/**
 * FASE 2 UI — Modal reutilizable de historial de auditoria.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open) => void
 *  - collection: 'deferrals' | 'aircraft' | 'flights' | 'sms_events' | 'maintenance'
 *  - entityId: string
 *  - entityLabel: string  // ej "Diferido MEL-22-01" o "HK-1234"
 */
export function AuditHistoryModal({ open, onOpenChange, collection, entityId, entityLabel }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !collection || !entityId) return;
        setLoading(true);
        setError(null);
        axios.get(`${API}/audit/${collection}/${entityId}`)
            .then(r => setEntries(r.data))
            .catch(e => setError(e.response?.data?.detail || 'Error al cargar el historial'))
            .finally(() => setLoading(false));
    }, [open, collection, entityId]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="audit-history-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <History className="w-5 h-5" /> Historial de cambios
                    </DialogTitle>
                    <DialogDescription>
                        Quien cambio que, y cuando — {entityLabel || entityId}
                    </DialogDescription>
                </DialogHeader>

                {loading && (
                    <div className="flex items-center justify-center py-12 text-muted-foreground">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando...
                    </div>
                )}

                {error && !loading && (
                    <div className="text-sm text-destructive py-6 text-center" data-testid="audit-error">{error}</div>
                )}

                {!loading && !error && entries.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground" data-testid="audit-empty">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">Aun no hay cambios registrados para este item.</p>
                    </div>
                )}

                {!loading && !error && entries.length > 0 && (
                    <div className="space-y-3" data-testid="audit-list">
                        {entries.map((e) => {
                            const meta = ACTION_LABELS[e.action] || { label: e.action, variant: 'outline' };
                            const changeCount = Object.keys(e.changes || {}).length;
                            return (
                                <div key={e.id} className="border rounded-lg p-3 bg-muted/20" data-testid={`audit-entry-${e.id}`}>
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Badge variant={meta.variant} className="shrink-0">{meta.label}</Badge>
                                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                                                <User className="w-3 h-3 shrink-0" />
                                                <span className="truncate font-medium text-foreground">{e.user_name || e.user_email || 'Sistema'}</span>
                                                {e.user_role && <span className="text-muted-foreground">·</span>}
                                                {e.user_role && <span className="capitalize">{e.user_role}</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                                            <Clock className="w-3 h-3" /> {fmtDate(e.created_at)}
                                        </div>
                                    </div>
                                    {changeCount > 0 && (
                                        <div className="mt-2 border-t border-border/40 pt-2 space-y-1.5">
                                            {Object.entries(e.changes).map(([field, diff]) => (
                                                <div key={field} className="text-xs grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                                                    <span className="font-mono font-bold text-primary self-start">{field}:</span>
                                                    <div className="min-w-0">
                                                        <span className="line-through text-muted-foreground break-words">{fmtValue(diff.before)}</span>
                                                        <span className="mx-1.5">→</span>
                                                        <span className="text-foreground break-words">{fmtValue(diff.after)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
